import type { LocationDescriptorObject } from 'history';
import { matchPath } from 'react-router-dom';

import { browserHistory } from '/app/history';

import { ROUTES } from '../../routes/config';
import { queryParamsToString } from '../util';

import type { LocationDescriptor, Route, RouteName, RouteParams } from './types';

export function getLocationDescriptorObject<R extends RouteName = RouteName>(
  to: LocationDescriptor<R>,

  // These options are based on global application state
  opts?: {
    routeParams?: RouteParams;
  },
): LocationDescriptorObject & { params?: RouteParams; external?: boolean } {
  const pathKey = typeof to === 'string' ? to : to.pathname;
  if (!(typeof pathKey === 'string' && pathKey in ROUTES)) {
    throw new Error(`Unknown route: ${pathKey}`);
  }

  const route = ROUTES[pathKey as RouteName];

  const redirectExternal = 'redirectExternal' in route ? route.redirectExternal : undefined;

  if (redirectExternal) {
    // External redirect returns full url
    const externalUrl = typeof redirectExternal === 'function' ? redirectExternal() : redirectExternal;

    if (externalUrl) {
      return {
        ...(typeof to === 'object' ? to : {}),

        pathname: externalUrl,
        external: true,
      };
    }
  }

  // Ensure we have params objects so that we can modify them
  const routeParams = typeof to === 'object' ? { ...to.params } : {};
  const queryParams = typeof to === 'object' ? { ...to.queryParams } : {};

  // If no site id was passed, try to use the current site id
  if (!routeParams.siteId && opts?.routeParams?.siteId) {
    routeParams.siteId = opts.routeParams.siteId;
  }

  // Get the path that matches our route params the best
  const pathname = getApplicablePath(route.path, Object.keys(routeParams) as (keyof typeof routeParams)[]);

  // Ensure that `to` is an object
  return {
    // Spread existing object if possible
    ...(typeof to === 'object' ? to : {}),

    pathname: replacePathParams(pathname, routeParams),
    params: routeParams,
    external: !!redirectExternal,
    search: queryParamsToString(queryParams),
  };
}

/**
 * Parses search part of an url and returns object with query parameters.
 */
export function getSearchQuery<T extends Record<string, unknown>>(search: string): T {
  return Array.from(new URLSearchParams(search).entries()).reduce<T>((prev, entry) => {
    const [name, val] = entry as [keyof T, T[keyof T]];
    prev[name] = val;
    return prev;
  }, {} as T);
}

/**
 * Replace text in url without refreshing the page
 */
export function replaceInUrl(search: string, replace: string) {
  browserHistory.replace({ pathname: window.location.pathname.replace(search, replace) });
}

/**
 * Update a url after creating a resource: replacing `/search` with the newly created id
 */
export function updateUrlForCreatedResource(newId: string) {
  replaceInUrl('/create', `/${newId}`);
}

export function findRouteByPathname(pathname: string) {
  let foundRoute: (Route & { name: RouteName }) | undefined;
  for (const routeName in ROUTES) {
    const route = ROUTES[routeName as RouteName];
    const match = matchPath(pathname, { path: route.path, exact: true });
    if (match) {
      foundRoute = { ...(route as Route), name: routeName as RouteName };
      break; // Stop searching
    }
  }
  return foundRoute;
}

const getMatchesLength = (path: string, params: string[]) => {
  let ret = 0;
  for (const param of params) {
    if (path.includes(`:${param}`)) {
      ret++;
    }
  }
  return ret;
};

/**
 * Find path that best matches given param keys.
 *
 * @example
 * (['/p/:siteId/', '/p', '/p/:siteId/:mediaId'], ['siteId', 'mediaId']) => '/p/:siteId/:mediaId'
 * (['/p/:siteId/', '/p', '/p/:siteId/:mediaId'], []) => '/p'
 *
 * @param paths
 * @param paramKeys
 */
export function getApplicablePath(paths: string[] | undefined, paramKeys: string[]) {
  if (typeof paths === 'undefined') {
    return undefined;
  }

  if (paramKeys.length === 0) {
    // Return first route that doesn't include a param or return default route
    const foundPath = paths.find((p) => !p.includes(':'));
    return foundPath || paths[0];
  }

  // Return route that matches most number of param keys
  return [...paths].sort((a, b) => {
    return getMatchesLength(b, paramKeys) - getMatchesLength(a, paramKeys);
  })[0];
}

export function replacePathParams(
  pathname: string | undefined,
  params: Record<string, string | undefined> | undefined,
) {
  if (typeof pathname === 'undefined') {
    return undefined;
  }

  let ret = pathname;

  if (typeof params === 'object') {
    Object.keys(params).forEach((paramKey) => {
      const paramValue = params[paramKey] ?? '';

      if (typeof ret === 'string' && ret.includes(`:${paramKey}`)) {
        ret = ret.replace(`:${paramKey}`, paramValue);
      }
    });
  }

  return ret;
}
